import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { message, Select, Table } from "antd";
import { IoMdCheckboxOutline } from "react-icons/io";

import "./styles.scss";
import uris from "../../../config/uris";
import SpinLoader from "../../../assets/component/loader/loader";
import { UpdateSitesAction, UpdateSitesReset } from "../../../store/slices/sync-external/customSources";
import { AuthDetailsAction, AuthDetailsReset } from "../../../store/slices/auth/authDetails";
import { SiteDetailsAction, SiteDetailsReset } from "../../../store/slices/sync-external/siteDetails";

export const sharepointLogin = () => {
    const authUrl = process.env.REACT_APP_MICROSOFT_AUTH_URL;
    const clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
    const baseUrl = process.env.REACT_APP_REDIRECT_URL;
    const redirectUrl = `${baseUrl}${uris?.settings}`;

    const SCOPE = encodeURIComponent([
        "openid",
        "profile",
        "offline_access",
        "Sites.Read.All",
        "Files.Read.All",
        "Sites.FullControl.All",
        "User.Read"
      ].join(" "));

    const url = `${authUrl}?client_id=${clientId}&response_type=code&redirect_uri=${redirectUrl}&response_mode=query&scope=${SCOPE}&prompt=consent`;
    window.location.href = url;
}

const SharepointSection = () => {
    const [sharepointData, setSharepointData] = useState([])
    const [siteOptions, setSiteOptions] = useState([])
    const [folderOptions, setFolderOptions] = useState([])
    const [selectedSite, setSelectedSite] = useState(null)
    const [selectedFolder, setSelectedFolder] = useState(null)
    const [syncedData, setSyncedData] = useState([])

    const location = useLocation()
    const dispatch = useDispatch()
    const { isLoading: siteUpdateLoading, data: siteUpdateData, error: siteUpdateError, success: siteUpdateSuccess } = useSelector((state) => state?.updateSites)
    const { isLoading: siteDetailsLoading, data: siteDetails, error: siteDetailsError } = useSelector((state) => state?.siteDetails)
    const { isLoading: authDetailsLoading, data: authDetails } = useSelector(state => state?.authDetails)

    const handleSelectSite = (e) => {
        setSelectedSite(e)
        setSelectedFolder(null)
    }

    const handleSelectFolder = (e) => {
        setSelectedFolder(e)
    }

    const resetOptions = () => {
        setSelectedFolder(null)
        setSelectedSite(null)
    }

    const saveSelectedSource = () => {
        dispatch(UpdateSitesReset())

        const selectedSiteDetails = siteOptions?.find((site) => site?.siteUrl === selectedSite)
        const selectedFolderDetails = folderOptions?.find((folder) => folder?.folderId === selectedFolder)

        if (selectedFolderDetails?.folderName && !siteUpdateLoading) {
            const payload = {
                siteUrl: selectedSiteDetails?.siteUrl,
                siteId: selectedSiteDetails?.siteId,
                siteName: selectedSiteDetails?.siteName,
                driveId: selectedFolderDetails?.driveId,
                folderId: selectedFolderDetails?.folderId,
                folderName: selectedFolderDetails?.folderName,
            }

            dispatch(UpdateSitesAction(payload))
        }
    }

    const tableHeaders = [
        {
            title: '',
            dataIndex: 'icon',
            key: 'icon',
            render: () => (<IoMdCheckboxOutline className="content-icon" />),
        },
        {
            title: 'Site',
            dataIndex: 'siteName',
            key: 'siteName',
        },
        {
            title: 'Folder',
            dataIndex: 'folderName',
            key: 'folderName',
        },
    ]

    useEffect(() => {
        dispatch(AuthDetailsReset())

        const queryParams = new URLSearchParams(location.search);
        const encryptedCode = queryParams.get("code");
        const baseUrl = process.env.REACT_APP_REDIRECT_URL;
        const redirectURI = `${baseUrl}${uris?.settings}`;

        const payload = {
            code: encryptedCode,
            redirectURI,
        }

        dispatch(AuthDetailsAction(payload))
    }, [])

    useEffect(() => {
        if (authDetails) {
            setSharepointData(authDetails?.data || []);
        }
    }, [authDetails])

    useEffect(() => {
        if (sharepointData) {
            const structuredSiteOptions = sharepointData?.map((item) => ({
                label: item?.siteName,
                value: item?.siteUrl,
                ...item,
            }))

            setSiteOptions(structuredSiteOptions)
        }
    }, [sharepointData])

    useEffect(() => {
        if (selectedSite) {
            const siteFolders = sharepointData?.find((item) => item?.siteUrl === selectedSite)
            const formattedOptions = siteFolders?.folders?.map((folder) => ({ label: folder?.folderName, value: folder?.folderId, ...folder }))

            setFolderOptions(formattedOptions)
        } else {
            setFolderOptions([])
        }
    }, [selectedSite])

    useEffect(() => {
        if (siteUpdateData?.message) {
            message?.success(siteUpdateData?.message)
        }
    }, [siteUpdateData?.message])

    useEffect(() => {
        if (siteUpdateError?.message) {
            message?.error(siteUpdateError?.message)
        }
    }, [siteUpdateError?.message])

    useEffect(() => {
        if (siteUpdateSuccess) {
            // Reset
            resetOptions()
            dispatch(UpdateSitesReset())
            dispatch(SiteDetailsReset())

            // Request
            dispatch(SiteDetailsAction())
        }
    }, [siteUpdateSuccess])

    useEffect(() => {
        if (siteDetails?.data?.sharepoint?.siteDetails) {
            setSyncedData([siteDetails?.data?.sharepoint?.siteDetails])
        } else {
            setSyncedData([])
        }
    }, [siteDetails])

    useEffect(() => {
        if (siteDetailsError?.message) {
            message?.error(siteDetailsError?.message)
            dispatch(SiteDetailsReset())
        }
    }, [siteDetailsError?.message])

    return (
        <div className="sharepoint-contents">
            {(authDetailsLoading || siteDetailsLoading) && (
                <div className="page-loader-wrapper">
                    <SpinLoader className="page-loader" />
                </div>
            )}

            {(!authDetailsLoading && !siteDetailsLoading) && (
                <>
                    {/* Options */}
                    <div className="options-container">
                        <div className="input-wrapper">
                            <div className="input-label">Sites</div>
                            <Select
                                showSearch
                                allowClear
                                className="input-main"
                                popupClassName="chat-react-select"
                                options={siteOptions}
                                onChange={handleSelectSite}
                                value={selectedSite}
                                placeholder="Select Site"
                                filterOption={(input, option) =>
                                    option?.label
                                        ?.toLowerCase()
                                        ?.includes(input?.toLowerCase())
                                }
                            />
                        </div>
                        {(folderOptions?.length === 0) && (
                            <div className="warning-message">
                                No folders available!
                            </div>
                        )}
                        {(folderOptions?.length > 0) && (
                            <>
                                <div className="input-wrapper">
                                    <div className="input-label">Folders</div>
                                    <Select
                                        showSearch
                                        allowClear
                                        className="input-main"
                                        popupClassName="chat-react-select"
                                        options={folderOptions}
                                        onChange={handleSelectFolder}
                                        value={selectedFolder}
                                        placeholder="Select Folder"
                                        filterOption={(input, option) =>
                                            option?.label
                                                ?.toLowerCase()
                                                ?.includes(input?.toLowerCase())
                                        }
                                    />
                                </div>
                                <div className="buttons-container">
                                    <div className="button-wrapper save-button" onClick={() => saveSelectedSource()}>
                                        {siteUpdateLoading ? "Loading..." : "Save Details"}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Selected Folders */}
                    <div className="synced-contents-section">
                        <div className="section-title">Selected Folders</div>
                        <div className="contents-wrapper">
                            {(syncedData?.length > 0) && (
                                <Table
                                    columns={tableHeaders}
                                    dataSource={syncedData}
                                    pagination={{ position: ["none"] }}
                                />
                            )}
                            {(syncedData?.length === 0) && "No selected folders available!"}
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}

export default SharepointSection